import React, { useMemo, useState } from 'react'
import { Avatar, Progress, Table, Tag } from 'antd'
import { Icon } from '@iconify/react'
import styles from './ProjectHealthDashboard.module.css'
import {
    formatHours,
    getInitials,
    getUtilizationPercent,
    getUtilizationTone,
} from './reports.utils'

const MemberHealthCard = ({ member }) => {
    const [expanded, setExpanded] = useState(false)

    const estimated = Number(member?.estimatedHours) || 0
    const logged = Number(member?.loggedHours) || 0
    const percent = getUtilizationPercent(logged, estimated)
    const tone = getUtilizationTone(percent)
    const tasks = Array.isArray(member?.tasks) ? member.tasks : []

    const taskColumns = useMemo(
        () => [
            {
                title: 'Task',
                dataIndex: 'title',
                key: 'title',
                ellipsis: true,
                render: (title) => (
                    <span className={styles.taskTitleCell}>{title}</span>
                ),
            },
            {
                title: 'Estimated',
                dataIndex: 'estimatedHours',
                key: 'estimatedHours',
                width: 110,
                align: 'right',
                render: (v) => `${formatHours(v)}h`,
            },
            {
                title: 'Logged',
                dataIndex: 'loggedHours',
                key: 'loggedHours',
                width: 110,
                align: 'right',
                render: (v) => `${formatHours(v)}h`,
            },
            {
                title: 'Utilization',
                key: 'util',
                width: 120,
                align: 'right',
                render: (_, row) => {
                    const p = getUtilizationPercent(
                        row.loggedHours,
                        row.estimatedHours
                    )
                    return (
                        <Tag
                            className={`${styles.utilTag} ${styles[`utilTag_${getUtilizationTone(p)}`]}`}
                        >
                            {p}%
                        </Tag>
                    )
                },
            },
        ],
        []
    )

    return (
        <div className={styles.memberCard}>
            <button
                type="button"
                className={styles.memberCardHeader}
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
            >
                <div className={styles.memberIdentity}>
                    <Avatar
                        size={40}
                        src={member?.profilePicture}
                        className={styles.memberAvatar}
                    >
                        {getInitials(member?.name)}
                    </Avatar>
                    <div className={styles.memberMeta}>
                        <span className={styles.memberName}>
                            {member?.name || 'Unassigned'}
                        </span>
                        <span className={styles.memberSub}>
                            {member?.taskCount ?? tasks.length} tasks
                        </span>
                    </div>
                </div>
                <div className={styles.memberStats}>
                    <div className={styles.memberStat}>
                        <span className={styles.memberStatLabel}>Est.</span>
                        <span className={styles.memberStatValue}>
                            {formatHours(estimated)}h
                        </span>
                    </div>
                    <div className={styles.memberStat}>
                        <span className={styles.memberStatLabel}>Logged</span>
                        <span className={styles.memberStatValue}>
                            {formatHours(logged)}h
                        </span>
                    </div>
                    <div className={styles.memberProgressWrap}>
                        <Progress
                            percent={Math.min(percent, 100)}
                            showInfo={false}
                            strokeColor={
                                tone === 'good'
                                    ? '#10b981'
                                    : tone === 'warn'
                                      ? '#f59e0b'
                                      : '#ef4444'
                            }
                            trailColor="#e2e8f0"
                            size="small"
                        />
                        <span
                            className={`${styles.memberPercent} ${styles[`memberPercent_${tone}`]}`}
                        >
                            {percent}%
                        </span>
                    </div>
                </div>
                <Icon
                    icon={
                        expanded
                            ? 'mdi:chevron-up'
                            : 'mdi:chevron-down'
                    }
                    width={20}
                    height={20}
                    className={styles.memberChevron}
                />
            </button>
            {expanded && tasks.length > 0 && (
                <div className={styles.memberTasks}>
                    <Table
                        size="small"
                        rowKey="taskId"
                        columns={taskColumns}
                        dataSource={tasks}
                        pagination={false}
                        scroll={{
                            x: 520,
                            y: tasks.length > 6 ? 280 : undefined,
                        }}
                    />
                </div>
            )}
            {expanded && tasks.length === 0 && (
                <div className={styles.memberEmptyTasks}>
                    No tasks assigned to this member.
                </div>
            )}
        </div>
    )
}

export default MemberHealthCard
